import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid,
  BookOpen,
  Quote,
  Settings as SettingsIcon,
  Inbox,
  LogOut,
  Trash2,
  Pencil,
  Plus,
  Mail,
  Check,
  KeyRound,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyCourse = { title: "", subtitle: "", description: "", level: "", duration: "", devanagari: "", order: 0 };
const emptyTestimonial = { name: "", role: "", quote: "", rating: 5, order: 0 };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [enquiries, setEnquiries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [e, c, t, s] = await Promise.all([
        api.get("/admin/enquiries"),
        api.get("/courses"),
        api.get("/testimonials"),
        api.get("/settings"),
      ]);
      setEnquiries(e.data);
      setCourses(c.data);
      setTestimonials(t.data);
      setSettings(s.data);
    } catch {
      toast.error("Failed to load data");
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const unread = enquiries.filter((e) => !e.is_read).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-white/80 backdrop-blur-xl z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-deva text-2xl text-primary">न</span>
            <span className="font-display font-extrabold tracking-tight">
              Navjeevan CMS
            </span>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            onClick={doLogout}
            className="gap-2 text-muted-foreground"
          >
            <LogOut size={16} /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <h1 className="font-display font-bold text-3xl tracking-tight mb-8">
          Content Studio
        </h1>

        <Tabs defaultValue="enquiries">
          <TabsList className="mb-8">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="gap-2">
              <Inbox size={15} /> Enquiries
              {unread > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground h-5 px-1.5">
                  {unread}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses" className="gap-2">
              <BookOpen size={15} /> Courses
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="gap-2">
              <Quote size={15} /> Testimonials
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="gap-2">
              <SettingsIcon size={15} /> Contact Info
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="gap-2">
              <KeyRound size={15} /> Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries">
            <EnquiriesPanel enquiries={enquiries} reload={loadAll} />
          </TabsContent>
          <TabsContent value="courses">
            <CoursesPanel courses={courses} reload={loadAll} />
          </TabsContent>
          <TabsContent value="testimonials">
            <TestimonialsPanel testimonials={testimonials} reload={loadAll} />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsPanel settings={settings} setSettings={setSettings} />
          </TabsContent>
          <TabsContent value="security">
            <ChangePasswordPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ---------------- Enquiries ---------------- */
function EnquiriesPanel({ enquiries, reload }) {
  const markRead = async (id) => {
    try {
      await api.patch(`/admin/enquiries/${id}/read`);
      reload();
    } catch {
      toast.error("Failed");
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Enquiry deleted");
      reload();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <LayoutGrid size={18} /> Enquiries ({enquiries.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {enquiries.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">No enquiries yet.</p>
        ) : (
          <Table data-testid="enquiries-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Course</TableHead>
                <TableHead>Message</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enquiries.map((e) => (
                <TableRow key={e._id} data-testid={`enquiry-row-${e._id}`} className={e.is_read ? "" : "bg-primary/5"}>
                  <TableCell className="font-medium">
                    {!e.is_read && <span className="inline-block w-2 h-2 rounded-full bg-primary mr-2" />}
                    {e.name}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{e.email}</div>
                    <div className="text-muted-foreground">{e.phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">{e.course || "—"}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{e.message || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <a href={`mailto:${e.email}`} className="inline-flex mr-1">
                      <Button size="icon" variant="ghost"><Mail size={15} /></Button>
                    </a>
                    {!e.is_read && (
                      <Button size="icon" variant="ghost" data-testid={`mark-read-${e._id}`} onClick={() => markRead(e._id)}>
                        <Check size={15} />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" data-testid={`delete-enquiry-${e._id}`} onClick={() => remove(e._id)}>
                      <Trash2 size={15} className="text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------- Courses ---------------- */
function CoursesPanel({ courses, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCourse);

  const openNew = () => { setEditing(null); setForm(emptyCourse); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyCourse, ...c }); setOpen(true); };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    delete payload._id;
    delete payload.created_at;
    try {
      if (editing) await api.put(`/admin/courses/${editing._id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success("Course saved");
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/courses/${id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="font-display flex items-center gap-2"><BookOpen size={18} /> Courses</CardTitle>
        <Button data-testid="add-course-btn" onClick={openNew} className="gap-2 rounded-full"><Plus size={15} /> Add Course</Button>
      </CardHeader>
      <CardContent>
        <Table data-testid="courses-table">
          <TableHeader>
            <TableRow>
              <TableHead>अ</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {courses.map((c) => (
              <TableRow key={c._id} data-testid={`course-row-${c._id}`}>
                <TableCell className="font-deva text-2xl text-primary">{c.devanagari}</TableCell>
                <TableCell className="font-medium">{c.title}<div className="text-xs text-muted-foreground">{c.subtitle}</div></TableCell>
                <TableCell>{c.level}</TableCell>
                <TableCell>{c.duration}</TableCell>
                <TableCell>{c.order}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" data-testid={`edit-course-${c._id}`} onClick={() => openEdit(c)}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-course-${c._id}`} onClick={() => remove(c._id)}><Trash2 size={15} className="text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="course-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} Course</DialogTitle><DialogDescription>Fill in the programme details shown on the public site.</DialogDescription></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Title"><Input data-testid="course-title" value={form.title} onChange={set("title")} /></Field>
              <Field label="Devanagari (अ)"><Input data-testid="course-devanagari" value={form.devanagari} onChange={set("devanagari")} /></Field>
            </div>
            <Field label="Subtitle"><Input value={form.subtitle} onChange={set("subtitle")} /></Field>
            <Field label="Description"><Textarea value={form.description} onChange={set("description")} rows={3} /></Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Level"><Input value={form.level} onChange={set("level")} /></Field>
              <Field label="Duration"><Input value={form.duration} onChange={set("duration")} /></Field>
              <Field label="Order"><Input type="number" value={form.order} onChange={set("order")} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-course-btn" onClick={save} className="bg-foreground text-background hover:bg-primary">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Testimonials ---------------- */
function TestimonialsPanel({ testimonials, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTestimonial);

  const openNew = () => { setEditing(null); setForm(emptyTestimonial); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...emptyTestimonial, ...t }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name || !form.quote) { toast.error("Name and quote required"); return; }
    const payload = { ...form, rating: Number(form.rating) || 5, order: Number(form.order) || 0 };
    delete payload._id; delete payload.created_at;
    try {
      if (editing) await api.put(`/admin/testimonials/${editing._id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Testimonial saved"); setOpen(false); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="font-display flex items-center gap-2"><Quote size={18} /> Testimonials</CardTitle>
        <Button data-testid="add-testimonial-btn" onClick={openNew} className="gap-2 rounded-full"><Plus size={15} /> Add</Button>
      </CardHeader>
      <CardContent>
        <Table data-testid="testimonials-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Quote</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {testimonials.map((t) => (
              <TableRow key={t._id} data-testid={`testimonial-row-${t._id}`}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>{t.role}</TableCell>
                <TableCell className="max-w-sm truncate text-sm">{t.quote}</TableCell>
                <TableCell>{t.rating}★</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" data-testid={`edit-testimonial-${t._id}`} onClick={() => openEdit(t)}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-testimonial-${t._id}`} onClick={() => remove(t._id)}><Trash2 size={15} className="text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="testimonial-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} Testimonial</DialogTitle><DialogDescription>Add a student or parent quote shown on the public site.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name"><Input data-testid="testimonial-name" value={form.name} onChange={set("name")} /></Field>
              <Field label="Role"><Input value={form.role} onChange={set("role")} /></Field>
            </div>
            <Field label="Quote"><Textarea data-testid="testimonial-quote" value={form.quote} onChange={set("quote")} rows={3} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Rating (1-5)"><Input type="number" min={1} max={5} value={form.rating} onChange={set("rating")} /></Field>
              <Field label="Order"><Input type="number" value={form.order} onChange={set("order")} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-testimonial-btn" onClick={save} className="bg-foreground text-background hover:bg-primary">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Settings ---------------- */
function SettingsPanel({ settings, setSettings }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  if (!form) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setSettings(data);
      toast.success("Contact info updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2"><SettingsIcon size={18} /> Contact Information</CardTitle>
      </CardHeader>
      <CardContent className="max-w-2xl space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Email"><Input data-testid="settings-email" value={form.email} onChange={set("email")} /></Field>
          <Field label="Phone"><Input data-testid="settings-phone" value={form.phone} onChange={set("phone")} /></Field>
          <Field label="WhatsApp"><Input value={form.whatsapp} onChange={set("whatsapp")} /></Field>
          <Field label="Hours"><Input value={form.hours} onChange={set("hours")} /></Field>
          <Field label="Instagram URL"><Input value={form.instagram} onChange={set("instagram")} /></Field>
          <Field label="Facebook URL"><Input value={form.facebook} onChange={set("facebook")} /></Field>
        </div>
        <Field label="Address"><Textarea value={form.address} onChange={set("address")} rows={2} /></Field>
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="rounded-full bg-foreground text-background hover:bg-primary h-11 px-8">
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </CardContent>
    </Card>
  );
}

/* ---------------- Security ---------------- */
function ChangePasswordPanel() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <KeyRound size={18} /> Change Password
        </CardTitle>
      </CardHeader>
      <CardContent className="max-w-md">
        <form onSubmit={submit} className="space-y-5">
          <Field label="Current password">
            <Input
              data-testid="change-password-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </Field>
          <Field label="New password">
            <Input
              data-testid="change-password-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              data-testid="change-password-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </Field>
          <Button
            data-testid="change-password-submit"
            type="submit"
            disabled={saving}
            className="rounded-full bg-foreground text-background hover:bg-primary h-11 px-8"
          >
            {saving ? "Saving..." : "Update password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-body text-xs text-muted-foreground mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
